<template>
    <suggest-buy v-if="adding" :symbol="trade.symbol" :etoro-trade="trade" @added="onChange" />

    <bottom-sheet v-else :dismiss-label="trade.event ? 'Close' : 'Cancel'">
        <template #title>
            <span>{{ trade.side === 'buy' ? 'Buy' : 'Sell' }} {{ trade.symbol }}</span>
            <!-- the title also drags the sheet, so a touch on the switch must not start a drag -->
            <span v-if="!trade.event" class="etoro-filter" @touchstart.stop>
                <label for="etoro-all-events" class="etoro-filter-label">{{ filterLabel }}</label>
                <b-form-checkbox id="etoro-all-events" v-model="allEvents" switch />
            </span>
        </template>

        <record-row strip>
            <div class="mono">{{ formatAmount(trade.value) }}</div>
            <div class="mono">{{ formatPrice(trade.price) }}</div>
            <div class="record-date">{{ formatTimeFromISO(trade.time) }}</div>
        </record-row>

        <sheet-section v-if="trade.event" label="Linked event">
            <record-row>
                <div class="mono">{{ amountText(trade.event) }}</div>
                <div class="mono">{{ priceText(trade.event) }}</div>
                <div class="record-date">{{ formatTimeFromISO(trade.event.timestamp) }}</div>
                <div>
                    <button type="button" class="sheet-text-btn sheet-text-btn-danger" @click="unlink">
                        <b-spinner v-if="busyEventId === trade.event.public_id" small />
                        <template v-else>Unlink</template>
                    </button>
                </div>
            </record-row>
        </sheet-section>

        <sheet-section v-else-if="eventsVisible" label="Events">
            <div v-if="loading" class="sheet-muted-row">
                <b-spinner small variant="light" />
            </div>

            <record-row v-for="event in shownEvents" :key="event.public_id" :tone="eventTone(event)">
                <div class="mono">
                    <b-icon :icon="eventIcon(event).icon" :class="eventIcon(event).class" />
                    {{ amountText(event) }}
                </div>
                <div class="mono">{{ priceText(event) }}</div>
                <div class="record-date">{{ formatTimeFromISO(event.timestamp) }}</div>
                <div>
                    <button type="button" class="sheet-text-btn" @click="link(event)">
                        <b-spinner v-if="busyEventId === event.public_id" small />
                        <template v-else>Link</template>
                    </button>
                </div>
                <template v-if="warrenStart(event)" #note>in Warren from {{ formatPrice(warrenStart(event)) }}</template>
            </record-row>
        </sheet-section>

        <template v-if="!trade.event && trade.side === 'buy'" #actions>
            <button type="button" class="sheet-btn sheet-btn-primary" :disabled="!!busyEventId" @click="adding = true">Add position</button>
        </template>
    </bottom-sheet>
</template>

<script>
import { mapState } from 'vuex';
import apiMixin from '@/mixins/api';
import formatter from '@/mixins/formatter';
import eventsMixin from '@/mixins/eventsMixin';
import SuggestBuy from '@/components/overlays/SuggestBuy.vue';
import BottomSheet from '@/components/sheet/BottomSheet.vue';
import SheetSection from '@/components/sheet/SheetSection.vue';
import RecordRow from '@/components/sheet/RecordRow.vue';
import { TRADE_SIDE_STYLES, EVENT_TRADE_SIDES } from '@/constants/trading';

// an eToro fill lands a little off the Warren event, so a match allows this much drift
const MATCH_PRICE_DRIFT = 0.02;
const MATCH_TIME_MS = 86400000;
const SHOWN_EVENTS = 5;

export default {
    name: 'EtoroTrade',
    props: ['trade', 'onChange', 'onLinkChange'],
    mixins: [apiMixin, formatter, eventsMixin],
    components: { SuggestBuy, BottomSheet, SheetSection, RecordRow },

    data() {
        return {
            events: [],
            adding: false,
            busyEventId: null,
            allEvents: false,
        };
    },
    mounted() {
        if (this.trade.event) return;

        this.loadEvents();
    },
    methods: {
        async loadEvents() {
            const response = await this.apiGet(
                '/api/etoro/events',
                { symbol: this.trade.symbol },
                { requestId: 'linkableEvents' }
            );
            if (!response) return;

            this.events = response.data.events;
        },
        // a linked event most likely recorded another fill, so it fades and is never the match
        eventTone(event) {
            if (event.has_linked_trades) return 'muted';
            // a buy back can link to an exit, but only an event on the side of the trade recorded this fill
            if (EVENT_TRADE_SIDES[event.subtype] !== this.trade.side) return null;

            if (Math.abs(this.trade.value - this.eventSize(event)) >= this.defaultPositionSize) return null;
            if (Math.abs(this.trade.price / this.eventPrice(event) - 1) > MATCH_PRICE_DRIFT) return null;
            if (Math.abs(this.dateFromISO(this.trade.time) - this.dateFromISO(event.timestamp)) > MATCH_TIME_MS) return null;

            return 'match';
        },
        // a linked event no longer needs its side, since the trades page shows the whole group
        eventIcon(event) {
            if (event.has_linked_trades) return { icon: 'link-45deg', class: 'text-info' };

            return TRADE_SIDE_STYLES[EVENT_TRADE_SIDES[event.subtype]];
        },
        // the API writes this starting price into the message of a buy recorded at a multiple of the fill
        warrenStart(event) {
            const data = event.data || {};
            if (!(data.multiplier > 1)) return null;

            return data.buy_price * data.multiplier;
        },
        amountText(event) {
            const amount = this.eventSize(event);
            return amount === null ? '' : this.formatAmount(amount);
        },
        priceText(event) {
            const price = this.eventPrice(event);
            return price === null ? '' : this.formatPrice(price);
        },
        async link(event) {
            if (this.busyEventId) return;
            this.busyEventId = event.public_id;

            const response = await this.apiPost('/api/etoro/link', { event_public_id: event.public_id, trade_id: this.trade.trade_id });
            // only a refusal frees the buttons, so the spinner stays while the sheet closes
            if (!response) {
                this.busyEventId = null;
                return;
            }

            this.onLinkChange(this.trade.trade_id, response.data.event);
            this.$FModal.hide();
        },
        async unlink() {
            if (this.busyEventId) return;
            this.busyEventId = this.trade.event.public_id;

            const response = await this.apiPost('/api/etoro/unlink', { trade_id: this.trade.trade_id });
            if (!response) {
                this.busyEventId = null;
                return;
            }

            this.onLinkChange(this.trade.trade_id, null);
            this.$FModal.hide();
        },
    },
    computed: {
        ...mapState({
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        loading() {
            return !!this.apiLoading['linkableEvents'];
        },
        // the API sends the buys and the exits of the last 30 days, newest first
        shownEvents() {
            const events = this.allEvents
                ? this.events
                : this.events.filter(event => EVENT_TRADE_SIDES[event.subtype] === this.trade.side);

            return events.slice(0, SHOWN_EVENTS);
        },
        filterLabel() {
            if (this.allEvents) return 'All events';

            return this.trade.side === 'buy' ? 'Buys only' : 'Exits only';
        },
        eventsVisible() {
            return this.loading || this.shownEvents.length > 0;
        },
    },
};
</script>

<style scoped>
.etoro-filter {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    align-self: center;
    font-size: 13px;
    font-weight: normal;
    color: var(--sheet-field);
}
.etoro-filter-label {
    margin: 0;
    cursor: pointer;
}
</style>
